import React from 'react';
import { SearchIcon } from 'lucide-react';
import { Container } from '../../components/ui/Primitives';
import { Button } from '../../components/ui/Button';
import { Photo } from '../../components/ui/Photo';
import { PHOTOS } from '../../data/photos';
import { useAcademyHub } from '../../context/AcademyHub';
import { CourseCard } from '../../components/academy/CourseCard';
import { Seo } from '../../components/Seo';
import { ACADEMY_LINES } from '../../data/academyCourses';
import { SECTORS } from '../../data/services';
import { inputClass } from '../../components/ui/Field';
import type { AcademyLine, Difficulty } from '../../types/academy';
const LEVELS: Difficulty[] = ['Beginner', 'Intermediate', 'Advanced'];
export function AcademyCatalogue() {
  const [line, setLine] = React.useState<AcademyLine | 'all'>('all');
  const [level, setLevel] = React.useState<Difficulty | ''>('');
  const [sector, setSector] = React.useState('');
  const [query, setQuery] = React.useState('');
  const {
    publishedCourses: published
  } = useAcademyHub();
  const flagship = published.find(c => c.flagship);
  const q = query.trim().toLowerCase();
  const filtered = published.filter(c => (line === 'all' || c.serviceLine === line) && (!level || c.difficulty === level) && (!sector || c.allSectors || c.sectors.includes(sector)) && (!q || c.title.toLowerCase().includes(q) || c.description.toLowerCase().includes(q) || c.modules.some(m => m.title.toLowerCase().includes(q) || m.lessons.some(l => l.toLowerCase().includes(q)))));
  const hasFilters = line !== 'all' || level || sector || q;
  const reset = () => {
    setLine('all');
    setLevel('');
    setSector('');
    setQuery('');
  };
  return <>
      <Seo title="Cavalla Academy, free courses in data, AI and public delivery" description="Free, practical courses in data analysis, AI governance, growth and public sector delivery for organisations across African and emerging markets." path="/academy" data-id="element-1494" data-mp="n6jbbf3mv1md" />

      <section data-id="element-1495" data-mp="7eqobc5azt1r">
        <Container className="grid gap-10 py-14 sm:py-20 lg:grid-cols-[1.2fr_0.8fr] lg:items-end" data-id="element-1496" data-mp="7ccraxr2c6rk">
          <div data-id="element-1497" data-mp="2djfravc6pu1">
            <h1 className="max-w-2xl font-display text-[36px] leading-[1.08] text-ink sm:text-[52px]" data-id="element-1498" data-mp="h4aaors8651d">
              Practical courses in data, AI and public delivery. All free.
            </h1>
            <p className="mt-6 max-w-xl text-[17px] text-body" data-id="element-1499" data-mp="q4yktbehzl8z">
              The methods behind Cavalla’s advisory work, taught for government teams, organisations and professionals across
              African and emerging markets. Start any course without signing up.
            </p>
            <form role="search" onSubmit={e => e.preventDefault()} className="mt-8 max-w-lg" data-id="element-1500" data-mp="100s8nqxo3162">
              <label htmlFor="academy-search" className="sr-only" data-id="element-1501" data-mp="v276hlyuu7ez">
                Search courses
              </label>
              <div className="relative" data-id="element-1502" data-mp="2wvob0grous5i">
                <SearchIcon className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-muted" aria-hidden="true" data-id="element-1503" data-mp="2wwpy5jlzb53l" />
                <input id="academy-search" type="search" value={query} onChange={e => setQuery(e.target.value)} placeholder="Search courses and lessons" className={`${inputClass} py-3.5 pl-11`} data-id="element-1504" data-mp="1cfqal3jrzghx" />
              </div>
            </form>
          </div>
          <div data-id="element-1505" data-mp="tlkz8uv1diwm">
          <Photo photo={PHOTOS.workshopTable} aspect="aspect-[16/10]" caption={false} eager data-id="element-1506" data-mp="toz4cav3467r" />
          <dl className="mt-6 grid grid-cols-3 gap-6 border-t border-line pt-6" data-id="element-1507" data-mp="xmd8vp7tlptf">
            <div data-id="element-1508" data-mp="jq9laergcfpx">
              <dt className="text-small text-muted" data-id="element-1509" data-mp="jqt4dysu9a5j">Courses</dt>
              <dd className="mt-1 font-display text-[32px] text-ink" data-id="element-1510" data-mp="ookt6jy6sg5i">{published.length}</dd>
            </div>
            <div data-id="element-1511" data-mp="2e3956k2v7w30">
              <dt className="text-small text-muted" data-id="element-1512" data-mp="2e0myemnstklu">Lessons</dt>
              <dd className="mt-1 font-display text-[32px] text-ink" data-id="element-1513" data-mp="2j0ri0lk06vga">
                {published.reduce((n, c) => n + c.modules.reduce((m, mod) => m + mod.lessons.length, 0), 0)}
              </dd>
            </div>
            <div data-id="element-1514" data-mp="e0zusvc0prjj">
              <dt className="text-small text-muted" data-id="element-1515" data-mp="dydo0xwybg2d">Cost</dt>
              <dd className="mt-1 font-display text-[32px] text-ink" data-id="element-1516" data-mp="9xtrnwk7cgha">Free</dd>
            </div>
          </dl>
          </div>
        </Container>
      </section>

      {flagship && !hasFilters && <section aria-labelledby="flagship" className="border-y border-line bg-mist" data-id="element-1517" data-mp="1qpiqpcohuc">
          <Container className="grid gap-8 py-10 sm:py-12 lg:grid-cols-[1fr_auto] lg:items-center" data-id="element-1518" data-mp="3rf4krjsqfbia">
            <div data-id="element-1519" data-mp="3mfi93esjy3bl">
              <p className="text-small font-medium text-gold" data-id="element-1520" data-mp="3mh32h134rg7v">Start here</p>
              <h2 id="flagship" className="mt-2 font-display text-[26px] leading-tight text-ink sm:text-[30px]" data-id="element-1521" data-mp="3ifhi6uo9nznn">
                {flagship.title}
              </h2>
              <p className="mt-3 max-w-2xl text-[15px] text-body" data-id="element-1522" data-mp="3dhfz49quc0c6">
                Score your own organisation across Connectivity, Compute, Context, Competency and Governance, then leave with a
                roadmap. {flagship.modules.length} modules, {flagship.hours} hours.
              </p>
            </div>
            <Button to={`/academy/courses/${flagship.slug}`} size="lg" data-id="element-1523" data-mp="1xwx7vb5zpweo">
              View the course
            </Button>
          </Container>
        </section>}

      <section aria-labelledby="catalogue" data-id="element-1524" data-mp="21w4zffdnccui">
        <Container className="py-14 sm:py-16" data-id="element-1525" data-mp="158i5pp7yj8kr">
          <div className="flex flex-col gap-5 lg:flex-row lg:items-center lg:justify-between" data-id="element-1526" data-mp="159ta9r988kcx">
            <h2 id="catalogue" className="font-display text-[24px] text-ink" data-id="element-1527" data-mp="1a7bbevh05vad">
              All courses
            </h2>
            <div className="flex flex-wrap items-center gap-3" data-id="element-1528" data-mp="1e6tsth9m7o8g">
              <div role="tablist" aria-label="Service line" className="flex flex-wrap gap-1 rounded-full border border-line p-1" data-id="element-1529" data-mp="1j0y2i8a9mad8">
                {[{
                id: 'all' as const,
                name: 'All'
              }, ...ACADEMY_LINES].map(l => <button key={l.id} type="button" role="tab" aria-selected={line === l.id} onClick={() => setLine(l.id)} className={`rounded-full px-4 py-1.5 text-[13px] transition-colors duration-150 ${line === l.id ? 'bg-gold text-charcoal' : 'text-body hover:text-ink'}`} data-id="element-1530" data-mp="qgzip7gokdrw">
                    {l.name}
                  </button>)}
              </div>
              <label htmlFor="academy-level" className="sr-only" data-id="element-1531" data-mp="15wu7h5b5woac">
                Level
              </label>
              <select id="academy-level" value={level} onChange={e => setLevel(e.target.value as Difficulty | '')} className={`${inputClass} w-auto py-2 text-[14px]`} data-id="element-1532" data-mp="1jwv4g9ob3bkj">
                <option value="" data-id="element-1533" data-mp="2vctmv45dj9yf">All levels</option>
                {LEVELS.map(l => <option key={l} value={l} data-id="element-1534" data-mp="2rbgi6xe0pct3">
                    {l}
                  </option>)}
              </select>
              <label htmlFor="academy-sector" className="sr-only" data-id="element-1535" data-mp="33sivfd0cttf9">
                Sector
              </label>
              <select id="academy-sector" value={sector} onChange={e => setSector(e.target.value)} className={`${inputClass} w-auto py-2 text-[14px]`} data-id="element-1536" data-mp="2ovi8dmt3ymn4">
                <option value="" data-id="element-1537" data-mp="3hk6123g6seap">All sectors</option>
                {SECTORS.map(s => <option key={s} value={s} data-id="element-1538" data-mp="3mhxq8ppg5mb7">
                    {s}
                  </option>)}
              </select>
            </div>
          </div>

          <p className="mt-4 text-small text-muted" aria-live="polite" data-id="element-1539" data-mp="3crw7mdfv4w3o">
            {filtered.length} {filtered.length === 1 ? 'course' : 'courses'}
          </p>

          {filtered.length ? <div className="mt-6 grid gap-5 sm:grid-cols-2 lg:grid-cols-3" data-id="element-1540" data-mp="93ue5z5hb880">
              {filtered.map(course => <CourseCard key={course.slug} course={course} data-id="element-1541" data-mp="257imqtaf08v0" />)}
            </div> : <div className="mt-6 rounded-md border border-dashed border-line px-6 py-14 text-center" data-id="element-1542" data-mp="20epkcc2f5cm3">
              <p className="font-display text-[19px] text-ink" data-id="element-1543" data-mp="1wengpxd4i946">No courses match those filters</p>
              <button type="button" onClick={reset} className="mt-3 text-[14px] text-royal underline underline-offset-4 hover:text-ink" data-id="element-1544" data-mp="1rh5gqwh3348b">
                Clear filters
              </button>
            </div>}
        </Container>
      </section>
    </>;
}